import {
  useCallback,
  useState,
  ChangeEvent,
  useContext,
  useEffect,
} from "react";
import { PasswordContext } from "../context";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { performLogin, setScreen } from "../../actions/loginActions";
import { useTranslation } from "react-i18next";
import { useErrorContext } from "../../_shared/context/ErrorContext";
import { ErrorType, ErrorTypeResetPsw } from "../../_shared/domain/errorType";
import {
  performForgetPassword,
  performResetPassword,
} from "@delorean/actions/resetPasswordActions";
import { ResetPasswordType } from "../schemas/resetPassword";
import { useNavigate } from "react-router-dom";
import { PasswordCriteriaKey, PasswordResetScreen } from "../types";
import { LoginScreen } from "@delorean/login";
import {
  closeToken,
  setUserAuthenticated,
  setUserId,
} from "@delorean/utils/seapUtils";

export const useResetPasswordForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(
    (state: RootStateOrAny) => state.login.isAuthenticated
  );
  const {
    confirmNewPassword,
    newPassword,
    setNewPassword,
    setConfirmNewPassword,
  } = useContext(PasswordContext);

  const setCurrentScreen = useCallback(
    (screenType: LoginScreen | PasswordResetScreen) => {
      dispatch(setScreen(screenType));
    },
    [dispatch]
  );
  const { error, loading, screen, userCode } = useSelector(
    (state: RootStateOrAny) => state.login
  );
  const onnewPasswordChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setNewPassword(e.target.value);
    },
    [setNewPassword]
  );
  const onconfirmNewPasswordChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setConfirmNewPassword(e.target.value);
    },
    [setConfirmNewPassword]
  );

  const submitResetTempPsw = async (values: ResetPasswordType) => {
    try {
      // Delay intenzionale prima di iniziare le operazioni SEAP
      await new Promise((resolve) => setTimeout(resolve, 500));
      setUserId(userCode);
      const moreTxi = await closeToken();
      dispatch(performResetPassword(values.newPassword, moreTxi));
      setUserAuthenticated(true);
    } catch (error) {
      if (error && typeof error === "object" && "message" in error) {
        throw new Error((error as { message: string }).message);
      } else {
        throw new Error("An unknown error occurred");
      }
    }
  };

  const submitResetForgetPsw = async (values: ResetPasswordType) => {
    try {
      // Delay intenzionale prima di iniziare le operazioni SEAP
      await new Promise((resolve) => setTimeout(resolve, 500));
      setUserId(userCode);
      const moreTxi = await closeToken();
      dispatch(performForgetPassword(values.newPassword, moreTxi));
      setUserAuthenticated(true);
    } catch (error) {
      if (error && typeof error === "object" && "message" in error) {
        throw new Error((error as { message: string }).message);
      } else {
        throw new Error("An unknown error occurred");
      }
    }
  };

  const passwordCriteria: {
    key: PasswordCriteriaKey;
    label: string;
    description: string;
  }[] = [
    {
      key: "length",
      label: t("resetPassword.rules.length"),
      description: t("accessibility.resetPassword.rules.descriptions.length"),
    },
    {
      key: "lowercase",
      label: t("resetPassword.rules.lowercase"),
      description: t(
        "accessibility.resetPassword.rules.descriptions.lowercase"
      ),
    },
    {
      key: "uppercase",
      label: t("resetPassword.rules.uppercase"),
      description: t(
        "accessibility.resetPassword.rules.descriptions.uppercase"
      ),
    },
    {
      key: "number",
      label: t("resetPassword.rules.number"),
      description: t("accessibility.resetPassword.rules.descriptions.number"),
    },
    {
      key: "special",
      label: t("resetPassword.rules.special"),
      description: t("accessibility.resetPassword.rules.descriptions.special"),
    },
    {
      key: "match",
      label: t("resetPassword.rules.match"),
      description: t("accessibility.resetPassword.rules.descriptions.match"),
    },
  ];

  /*   useEffect(() => {
    if (isAuthenticated) {
      setCurrentScreen(PasswordResetScreen.APP_SCA)
    }
  }, [isAuthenticated]); */

  useEffect(() => {
    if (error) {
      console.log(error);
      if (
        error === ErrorTypeResetPsw.noAuthMethod ||
        error === ErrorTypeResetPsw.appNotEnrolled ||
        error === ErrorTypeResetPsw.appNotEnrolledScaGeneration
      ) {
        setCurrentScreen(PasswordResetScreen.APP_NOT_ENROLLED);
      } else {
        setCurrentScreen(PasswordResetScreen.GENERIC_FAILURE);
      }
    }
  }, [error]);
  return {
    onnewPasswordChange,
    onconfirmNewPasswordChange,
    loading,
    submitResetTempPsw,
    submitResetForgetPsw,
    passwordCriteria,
    screen,
  };
};
